import { formatBrlCsv, maskCep, maskCpfCnpjDigits } from '@/utils/nfseHomologacaoFormat';
import {
  OP_SIMP_NAC_LABEL,
  REG_AP_TRIB_SN_LABEL,
  TP_AMB_LABEL,
  TP_RET_ISSQN_LABEL,
  TRIB_ISSQN_LABEL,
  formatCTribNac,
  formatIbge,
  formatNbs
} from './nacionalDanfseLabels';

export type DanfsePreviewData = {
  tpAmb: string;
  tipoAmbienteLabel: string;
  municipio: string;
  idDps: string;
  nDps: string;
  serie: string;
  competencia: string;
  dhEmissao: string;
  prestador: {
    cnpj: string;
    im: string;
    nome: string;
    municipio: string;
    ibgeCep: string;
    endereco: string;
    email: string;
    simples: string;
    regime: string;
  };
  tomador: {
    doc: string;
    nome: string;
    municipio: string;
    ibgeCep: string;
    endereco: string;
    email: string;
  };
  servico: {
    cTribNac: string;
    cNbs: string;
    local: string;
    titulo: string;
    descricao: string;
  };
  trib: {
    tipo: string;
    incidencia: string;
    bc: string;
    aliquota: string;
    retencao: string;
    issqn: string;
  };
  ibsCbs: { cstClass: string; indOp: string } | null;
  totais: { valor: string; liquido: string };
  retencoesTotal: string;
};

const dash = '-';
const s = (v: unknown) => (v == null ? '' : String(v).trim());

function brDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}
function brDateTime(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2}:\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}` : iso;
}

/**
 * Monta os dados da prévia do DANFSe (NFS-e Nacional) a partir da prévia do billing.
 * Só devolve dados quando a prévia é do provedor Nacional e está válida.
 */
export function buildNacionalDanfseProps(args: {
  amountCents: number;
  preview: Record<string, unknown> | null | undefined;
  billingCfg: Record<string, unknown> | null | undefined;
}): DanfsePreviewData | null {
  const { preview, billingCfg, amountCents } = args;
  if (!preview || preview.emissionMode !== 'nacional' || preview.success !== true) return null;
  const nac = (preview.nacional || {}) as Record<string, unknown>;
  const fiscal = nac.fiscal as Record<string, unknown> | undefined;
  const dps = nac.dps as Record<string, unknown> | undefined;
  if (!fiscal || !dps) return null;

  const params = ((preview.payload as Record<string, unknown> | undefined)?.issueParams ||
    {}) as Record<string, unknown>;
  const svc = (Array.isArray(params.services) ? params.services[0] : null) as Record<
    string,
    unknown
  > | null;
  const bnf = ((billingCfg?.interBeneficiaryFinal as Record<string, unknown>) || {}) as Record<
    string,
    unknown
  >;
  const addr = (params.customerAddress || {}) as Record<string, unknown>;

  const prestCidade = s(bnf.cidade);
  const prestUf = s(bnf.uf);
  const municipio = prestCidade ? `${prestCidade} - ${prestUf}` : dash;

  const cents = Math.round(Number(amountCents) || 0);
  const valor = `R$ ${formatBrlCsv(cents)}`;
  const rate = typeof fiscal.issRatePercent === 'number' ? (fiscal.issRatePercent as number) : null;
  const issCents = rate != null ? Math.round((cents * rate) / 100) : null;

  const ibs = fiscal.ibsCbs as Record<string, unknown> | null;
  const sameLocal = s(fiscal.cLocPrestacao) === s(fiscal.cLocEmi);

  const tomCity = s(addr.city);
  const tomUf = s(addr.state);
  const tomLine = [s(addr.street), s(addr.number), s(addr.neighborhood)].filter(Boolean).join(', ');

  const pAddr = [s(bnf.endereco), s(bnf.bairro)].filter(Boolean).join(', ');

  return {
    tpAmb: s(nac.tpAmb),
    tipoAmbienteLabel: TP_AMB_LABEL[s(nac.tpAmb)] ?? s(nac.tpAmb),
    municipio,
    idDps: s(nac.idDps),
    nDps: s(dps.nDps),
    serie: s(dps.serie),
    competencia: brDate(s(dps.dCompet)),
    dhEmissao: brDateTime(s(dps.dhEmi)),
    prestador: {
      cnpj: maskCpfCnpjDigits(s(fiscal.prestadorCnpj)),
      im: s(fiscal.inscricaoMunicipal) || dash,
      nome: s(bnf.nome) || dash,
      municipio: prestCidade ? `${prestCidade} / ${prestUf}` : dash,
      ibgeCep: `${formatIbge(s(fiscal.cLocEmi))} / ${s(bnf.cep) ? maskCep(s(bnf.cep)) : dash}`,
      endereco: pAddr || dash,
      email: s(bnf.email) || dash,
      simples: OP_SIMP_NAC_LABEL[s(fiscal.opSimpNac)] ?? dash,
      regime: s(fiscal.regApTribSn) ? (REG_AP_TRIB_SN_LABEL[s(fiscal.regApTribSn)] ?? dash) : dash
    },
    tomador: {
      doc: maskCpfCnpjDigits(s(params.customerCnpjCpf)),
      nome: s(params.customerName) || dash,
      municipio: tomCity ? `${tomCity} / ${tomUf}` : dash,
      ibgeCep: `${s(addr.ibgeCode) ? formatIbge(s(addr.ibgeCode)) : dash} / ${s(addr.zipCode) ? maskCep(s(addr.zipCode)) : dash}`,
      endereco: tomLine || dash,
      email: s(params.customerEmail) || dash
    },
    servico: {
      cTribNac: formatCTribNac(s(fiscal.cTribNac)) + ' / ' + (s(fiscal.cTribMun) || dash),
      cNbs: formatNbs(s(fiscal.cNbs)),
      local:
        sameLocal && prestCidade
          ? `${prestCidade} / ${prestUf} / -`
          : `IBGE ${formatIbge(s(fiscal.cLocPrestacao))}`,
      titulo: s(fiscal.serviceName),
      descricao: s(svc?.description)
    },
    trib: {
      tipo: TRIB_ISSQN_LABEL[s(fiscal.tribIssqn)] ?? dash,
      incidencia: municipio === dash ? dash : `${prestCidade} / ${prestUf} / -`,
      bc: valor,
      aliquota: rate != null ? `${rate.toFixed(2).replace('.', ',')} %` : dash,
      retencao: TP_RET_ISSQN_LABEL[s(fiscal.tpRetIssqn)] ?? dash,
      issqn: issCents != null ? `R$ ${formatBrlCsv(issCents)}` : dash
    },
    ibsCbs: ibs ? { cstClass: `${s(ibs.cst)} / ${s(ibs.cClassTrib)}`, indOp: s(ibs.cIndOp) } : null,
    totais: { valor, liquido: valor },
    retencoesTotal: 'R$ 0,00'
  };
}
